import type { TFunction } from 'i18next';
import { formatDateTime, formatDay, formatTime, type Instant } from '../../format.js';
import { accountLimits, userActorOf } from '../settings/user-rights.js';
import type { CrewCardResult } from './types.js';

/**
 * Crew ▸ Login Accounts (ADR 0147): tek okuma ucunun (`GET /api/office/login-accounts`) görünümü ve
 * ekran metinleri. Durumları sunucu hesaplar (`@felix/db` `onBoardState`); burada yalnız metne
 * çevrilir.
 */

export type OnBoardState =
  | 'ready'
  | 'waiting'
  | 'noServer'
  | 'disabled'
  | 'noPassword'
  /**
   * Ofiste parola düştü (ADR 0150 §6) ama gemi henüz uygulamadı: gemide hâlâ girilebilir; ofiste
   * yeni parola verilmiş olsa da (ADR 0150 Ek).
   */
  | 'noPasswordPending'
  | 'noLogin'
  | 'joins'
  | 'signedOff'
  | 'disabledPending'
  | 'signedOffPending'
  | 'removed';

/** Katılışta hesabın gemide girişi engelleyen durumu (ADR 0147 Ek). */
export type OnBoardBlocker = 'noLogin' | 'disabled' | 'noPassword';

export interface LoginAccountVessel {
  vesselId: string;
  vesselName: string;
  roleId: string;
  rank: string;
  validFrom: string;
  validTo: string | null;
  state: OnBoardState;
  synced: boolean | null;
  /**
   * Gemiye inmemiş değişiklik: `at` en yenisi (metindeki saat), `since` en eskisi (takılma bundan
   * ölçülür; Ek E3, E8).
   */
  pending: { reason: 'password' | 'account'; at: string; since: string } | null;
  /** Katılışta hesap engeli (`joins` dışında `null`). */
  blocker: OnBoardBlocker | null;
  /** Gemi bağlantıda ama en eski bekleyen değişikliği 15 dakikadır uygulamıyor. */
  stuck: boolean;
}

export interface LoginAccountItem {
  userId: string;
  fullName: string;
  email: string;
  hasLogin: boolean;
  type: 'office' | 'vessel' | 'both';
  officeRoles: string[];
  active: boolean;
  mustChangePassword: boolean;
  totpEnrolled: boolean;
  /** Bugünkü şirket rolleri `settings.edit` filo veriyor (Settings ▸ Users'ın `admin`'i). */
  admin: boolean;
  /**
   * Yönetici hesabı (ADR 0150 §3 `isAdminAccount`): bugün ya da ileride yönetici rolü, şirket
   * rolüyle ya da gemi atamasıyla. Hesap işlemlerini ve hesap açmayı yalnız yönetici yapar (U39).
   * Eski sunucu göndermez.
   */
  adminAccount?: boolean;
  rowVersion: number;
  crewCard: boolean;
  passwordSetAt: string | null;
  passwordSetBy: 'user' | 'other' | null;
  /**
   * Geçici parola yönetici hesabına geçişte iptal edildi (ADR 0150 §6): `passwordSetAt` iptal
   * anıdır; yönetici yeni parola verene dek kişi giremez. Eski sunucu göndermez.
   */
  passwordDropped?: boolean;
  vessels: LoginAccountVessel[];
}

export interface LoginAccountVesselStatus {
  id: string;
  name: string;
  server: boolean;
  /** Gemi sunucusunun son çekme isteği: son bağlantı (son uygulama değil). */
  lastSyncAt: string | null;
  /** Geminin bugünkü yerel tarihi: gemide giriş bu güne bakar. */
  shipDate: string;
}

export interface LoginAccountList {
  /** Bulutun UTC günü. */
  today: string;
  vessels: LoginAccountVesselStatus[];
  accounts: LoginAccountItem[];
  canCreate: boolean;
  canEdit: boolean;
  canManage: boolean;
  /** İşlemi yapan (ADR 0151 §3): kendi satırı ayrılır; eski sunucu göndermez. */
  actorId?: string;
  /** İşlemi yapan şirket yöneticisi mi (`settings.edit` filo; U39); eski sunucu göndermez. */
  actorIsAdmin?: boolean;
}

/**
 * Sayfanın veri kaynağı: okuma tek uç; yazmalar Settings ▸ Users ve personel kartının var olan
 * uçlarıdır (ADR 0145, ADR 0131 Ek 1).
 */
export interface LoginAccountsSource {
  list(withoutLogin: boolean): Promise<LoginAccountList>;
  resetPassword(
    userId: string,
    rowVersion: number,
    password: string,
  ): Promise<{
    temporaryPassword: string;
    /** `active`: sıfırlama devre dışı kişiyi etkinleştirmez (ADR 0150 §5). */
    user: { fullName: string; email: string; active?: boolean };
  }>;
  changeEmail(
    userId: string,
    rowVersion: number,
    email: string,
    password: string,
  ): Promise<unknown>;
  setActive(
    userId: string,
    rowVersion: number,
    active: boolean,
    password: string,
  ): Promise<unknown>;
  resetTotp(userId: string, password: string): Promise<unknown>;
  createLogin(userId: string, email: string, password: string): Promise<CrewCardResult>;
}

export type LoginAccountAction =
  | 'newPassword'
  | 'changeEmail'
  | 'resetTotp'
  | 'disable'
  | 'enable'
  | 'createLogin';

/** Satırın işlem yargısının girdisi: hesap ve yönetici hesabı (yeni alanlar eski sunucuda yok). */
export type LoginAccountRowInput = Pick<LoginAccountItem, 'hasLogin' | 'active' | 'totpEnrolled'> &
  Partial<Pick<LoginAccountItem, 'userId' | 'admin' | 'adminAccount'>>;

/** Listenin yetkileri ve işlemi yapan (eski sunucu işlemi yapanı göndermez). */
export type LoginAccountRights = Pick<
  LoginAccountList,
  'canCreate' | 'canEdit' | 'canManage' | 'actorId' | 'actorIsAdmin'
>;

/**
 * Satırda sunucunun geçireceği hesap işleri (Settings ▸ Users ile aynı yargı, `accountLimits`):
 * yönetici hesabında (sunucunun `adminAccount`'u; eski sunucuda bugünkü `admin`) yalnız yönetici;
 * kişi kendini devre dışı bırakamaz.
 */
function loginAccountLimits(account: LoginAccountRowInput, rights: LoginAccountRights) {
  return accountLimits(
    {
      id: account.userId ?? '',
      adminAccount: account.admin === true || account.adminAccount === true,
    },
    userActorOf(rights),
  );
}

/**
 * Satırın işlemleri, Settings ▸ Users'ın yetkileriyle (ADR 0145, ADR 0042 §23): e-posta
 * `users.edit`; parola, iki adım, devre dışı/etkin `users.void`; hesap açma `users.create`. Hesap
 * işlemleri ve hesap açma kişi satırını yazar: ayrıca `users.edit` (ADR 0151 Ek).
 * İki adım isteğe bağlıdır (ADR 0148): "Turn off 2FA" yalnız açık hesapta.
 * Yazma yolunun kuralı da uygulanır (ADR 0150 §3; ADR 0151 §3): yönetici hesabında bunların
 * hepsi (hesap açma dahil) yalnız yöneticiden; kişinin kendi satırında devre dışı bırakma yok.
 */
export function loginAccountActions(
  account: LoginAccountRowInput,
  rights: LoginAccountRights,
): LoginAccountAction[] {
  const limits = loginAccountLimits(account, rights);
  if (!account.hasLogin) {
    return rights.canCreate && rights.canEdit && limits.account ? ['createLogin'] : [];
  }
  const manage = rights.canManage && rights.canEdit && limits.account;
  return [
    ...(manage ? (['newPassword'] as const) : []),
    ...(rights.canEdit && limits.account ? (['changeEmail'] as const) : []),
    ...(manage && account.totpEnrolled ? (['resetTotp'] as const) : []),
    ...(manage && !account.active ? (['enable'] as const) : []),
    ...(manage && account.active && limits.disable ? (['disable'] as const) : []),
  ];
}

/**
 * Satır menüsünde neden işlem eksik (Settings ▸ Users'ın notu ve metni, `setup.users.menuNote`):
 * yönetici hesabı (yalnız şirket yöneticisi) ya da kişinin kendi hesabı (devre dışı bırakamaz).
 * Yalnız işlemi yapanın yetkisinin verdiği bir işlem gizlendiyse not var.
 */
export function loginAccountNote(
  account: LoginAccountRowInput,
  rights: LoginAccountRights,
): 'adminAccount' | 'ownAccount' | null {
  const { canCreate, canEdit, canManage } = rights;
  const granted = loginAccountActions(account, { canCreate, canEdit, canManage });
  if (granted.length === loginAccountActions(account, rights).length) return null;
  return loginAccountLimits(account, rights).account ? 'ownAccount' : 'adminAccount';
}

/**
 * İşlemden sonraki bildirim: gemiye inen değişiklik (e-posta, devre dışı, etkin) gemide bir
 * sonraki senkronla geçerli olur (ADR 0147 Ek E1, E9).
 */
export function loginAccountNotice(
  t: TFunction,
  action: Exclude<LoginAccountAction, 'newPassword'>,
  name: string,
): string {
  const key = {
    changeEmail: 'email',
    resetTotp: 'totp',
    disable: 'disabled',
    enable: 'enabled',
    createLogin: 'created',
  }[action];
  return t(`loginAccounts.notice.${key}`, { name });
}

/** Ertesi gün (`YYYY-MM-DD`). */
export function nextDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export type LoginAccountTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/** Anın yerel günü `GG.AA.YYYY` (tarayıcının saati; `formatDateTime` ile aynı). */
const localDay = (at: Instant) => formatDateTime(at).slice(0, 10);

/**
 * Gemi satırının rozet tonu ve iki satırlık metni (ana durum ve ayrıntı). `now`: bekleyen
 * değişikliğin bugün olup olmadığı (ADR 0147 Ek) ve geminin tarihinin tarayıcının gününden farklı
 * olup olmadığı (ADR 0154 §3).
 */
export function onBoardText(
  t: TFunction,
  line: LoginAccountVessel,
  vessel: Pick<LoginAccountVesselStatus, 'lastSyncAt' | 'shipDate'> | undefined,
  options: { now?: Date } = {},
): { tone: LoginAccountTone; text: string; detail: string | null } {
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`loginAccounts.onBoard.${key}`, { vessel: line.vesselName, ...values });
  const now = options.now ?? new Date();
  // Başka gündeki değişiklik bugünkü gibi okunmasın: bugün değilse tarih ve saat (Ek, L4)
  const when = (at: string) =>
    localDay(at) === localDay(now) ? formatTime(at) : formatDateTime(at);
  // Gemi çekiyor ama en eski bekleyen değişikliği uygulamıyor (Ek, L3, E3, E8): ne zamandan beri ve
  // son bağlantı; yoksa yalnız son bağlantı (zaman çekme isteğinindir, son uygulamanın değil)
  const stuck =
    line.stuck && line.pending && vessel?.lastSyncAt
      ? k('stuck', { time: when(line.pending.since), contact: when(vessel.lastSyncAt) })
      : null;
  const contact =
    stuck ??
    (vessel?.lastSyncAt
      ? k('lastContact', { time: formatDateTime(vessel.lastSyncAt) })
      : k('neverSynced'));
  // Başlayacak kişinin hesabı katılıştan önce gemide olmalı (ADR 0144 sahip kararı); hesap
  // gemide girişe uygun değilse engeli söylenir (Ek, L5)
  const account =
    line.blocker === 'disabled'
      ? k(line.synced === false ? 'blocker.disabledPending' : 'blocker.disabled')
      : line.blocker
        ? k(`blocker.${line.blocker}`)
        : line.synced === null
          ? null
          : line.synced
            ? k('accountOnBoard')
            : k('accountNotOnBoard');
  const joins = k('joins', { date: formatDay(line.validFrom) });
  const signedOff = line.validTo
    ? k('signedOff', { date: formatDay(line.validTo), from: formatDay(nextDay(line.validTo)) })
    : null;
  const parts = (...items: (string | null)[]) => items.filter(Boolean).join(' · ');
  // Gemide giriş geminin gününe bakar (ADR 0154 §3): gemi tarihi tarayıcının gününden farklıysa
  // gün içeren satırda gemi tarihi
  const shipDay =
    vessel && formatDay(vessel.shipDate) !== localDay(now)
      ? k('shipDate', { date: formatDay(vessel.shipDate) })
      : null;
  switch (line.state) {
    case 'ready':
      return {
        tone: 'success',
        text: k('ready'),
        detail:
          parts(shipDay, line.validTo ? k('until', { date: formatDay(line.validTo) }) : null) ||
          null,
      };
    case 'waiting':
      return {
        tone: 'warning',
        text:
          line.pending?.reason === 'password'
            ? k('waitingPassword', { time: when(line.pending.at) })
            : line.pending
              ? k('waitingChange', { time: when(line.pending.at) })
              : k('waiting'),
        detail: contact,
      };
    case 'noServer':
      return { tone: 'neutral', text: k('noServer'), detail: k('noServerDetail') };
    case 'disabled':
      return { tone: 'danger', text: k('disabled'), detail: null };
    // Erişimi kaldıran değişiklik gemiye inmedi: gemideki kopya hâlâ giriş verebilir (Ek, M1)
    case 'disabledPending':
      return { tone: 'warning', text: k('disabledPending'), detail: contact };
    case 'signedOffPending': {
      // Gemideki kopya bugün sürüyor; ofiste görev bitti ya da başlangıcı ileri alındı (Ek E1, E7)
      const shipDate = vessel?.shipDate ?? line.validFrom;
      return {
        tone: 'warning',
        text:
          line.validTo === null || line.validFrom > shipDate
            ? k('joinsPending', { date: formatDay(line.validFrom) })
            : k('signedOffPending', { date: formatDay(line.validTo) }),
        detail: parts(shipDay, contact),
      };
    }
    case 'removed':
      return { tone: 'warning', text: k('removed'), detail: contact };
    case 'noPassword':
      return { tone: 'danger', text: k('noPassword'), detail: null };
    // Düşen parola gemiye inmedi: gemideki kopya eski parolayla hâlâ giriş verir (ADR 0150 §6)
    case 'noPasswordPending':
      return { tone: 'warning', text: k('noPasswordPending'), detail: contact };
    case 'noLogin':
      return { tone: 'neutral', text: k('noLogin'), detail: null };
    case 'joins':
      return { tone: 'info', text: k('notToday'), detail: parts(shipDay, joins, account, stuck) };
    case 'signedOff':
      return { tone: 'neutral', text: k('notToday'), detail: parts(shipDay, signedOff) || null };
  }
}

/**
 * Parola sütunu: geçici mi, kişi mi değiştirdi, ne zaman; yönetici hesabına geçişte iptal edilen
 * geçici parola (ADR 0150 §6) yeni parola sanılmaz.
 */
export function passwordText(
  t: TFunction,
  account: Pick<
    LoginAccountItem,
    'hasLogin' | 'mustChangePassword' | 'passwordSetAt' | 'passwordSetBy' | 'passwordDropped'
  >,
): { text: string; tone: LoginAccountTone } {
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`loginAccounts.password.${key}`, options);
  if (!account.hasLogin) return { text: '—', tone: 'neutral' };
  if (account.passwordDropped === true) {
    return account.passwordSetAt
      ? { text: k('cancelledAt', { time: formatDateTime(account.passwordSetAt) }), tone: 'danger' }
      : { text: k('cancelled'), tone: 'danger' };
  }
  if (account.mustChangePassword) {
    return account.passwordSetAt
      ? { text: k('temporaryAt', { time: formatDateTime(account.passwordSetAt) }), tone: 'warning' }
      : { text: k('temporary'), tone: 'warning' };
  }
  if (account.passwordSetBy === 'user' && account.passwordSetAt) {
    return { text: k('byUserOn', { date: localDay(account.passwordSetAt) }), tone: 'success' };
  }
  return account.passwordSetAt
    ? { text: k('setOn', { date: localDay(account.passwordSetAt) }), tone: 'neutral' }
    : { text: k('set'), tone: 'neutral' };
}

export interface LoginAccountFilter {
  query: string;
  vesselId: string | null;
  type: 'all' | 'office' | 'vessel';
  status: 'all' | 'active' | 'disabled' | 'noLogin';
}

/** Süzgeçler: gemi (o gemide ataması olan), tür ("Office + vessel" ikisinde de), durum, arama. */
export function filterLoginAccounts(
  accounts: readonly LoginAccountItem[],
  filter: LoginAccountFilter,
): LoginAccountItem[] {
  const q = filter.query.trim().toLocaleLowerCase();
  return accounts.filter(
    (a) =>
      (filter.vesselId === null || a.vessels.some((v) => v.vesselId === filter.vesselId)) &&
      (filter.type === 'all' || a.type === filter.type || a.type === 'both') &&
      (filter.status === 'all' ||
        (filter.status === 'noLogin'
          ? !a.hasLogin
          : a.hasLogin && (filter.status === 'active') === a.active)) &&
      (!q || a.fullName.toLocaleLowerCase().includes(q) || a.email.toLocaleLowerCase().includes(q)),
  );
}
